import { REGION_CODES } from '../../core/constants/referentials';
import { UpcomingEvent, UpcomingEventRegistrationStatus, UpcomingEventStatus } from '../../core/models';
import { BadgeTone } from '../ui/feedback/status-badge/status-badge';

/** Valeurs ouvertes côté backend (String libre, pas un enum fermé) — libellés FR + repli titre-casse. */
const EVENT_TYPE_LABELS: Record<string, string> = {
  BOOTCAMP: 'Bootcamp',
  TRAINING: 'Formation',
  MEETUP: 'Rencontre',
  FORUM: 'Forum',
  WEBINAR: 'Webinaire',
  WORKSHOP: 'Atelier',
  CONFERENCE: 'Conférence',
};

const CATEGORY_LABELS: Record<string, string> = {
  DIGITAL: 'Numérique',
  ENTREPRENEURSHIP: 'Entrepreneuriat',
  FINANCING: 'Financement',
  GREEN_ECONOMY: 'Économie verte',
  INNOVATION: 'Innovation',
  MENTORSHIP: 'Mentorat',
  NETWORKING: 'Réseautage',
  CITIZENSHIP: 'Engagement citoyen',
  TRAINING: 'Formation',
  YOUTH: 'Jeunesse',
};

const REGISTRATION_STATUS_LABELS: Record<UpcomingEventRegistrationStatus, string> = {
  REGISTERED: 'Inscrit(e)',
  WAITLISTED: "Liste d'attente",
  CANCELLED: 'Annulée',
  CHECKED_IN: 'Présent(e)',
};

const REGISTRATION_STATUS_TONES: Record<UpcomingEventRegistrationStatus, BadgeTone> = {
  REGISTERED: 'success',
  WAITLISTED: 'warning',
  CANCELLED: 'neutral',
  CHECKED_IN: 'info',
};

const EVENT_STATUS_LABELS: Record<UpcomingEventStatus, string> = {
  DRAFT: 'Brouillon',
  PUBLISHED: 'Publié',
  UNPUBLISHED: 'Dépublié',
  CANCELLED: 'Annulé',
  ARCHIVED: 'Archivé',
};

const EVENT_STATUS_TONES: Record<UpcomingEventStatus, BadgeTone> = {
  DRAFT: 'neutral',
  PUBLISHED: 'success',
  UNPUBLISHED: 'warning',
  CANCELLED: 'danger',
  ARCHIVED: 'neutral',
};

function reverseLookup(map: Record<string, string>, value: string): string {
  const entry = Object.entries(map).find(([, code]) => code === value);
  return entry?.[0] ?? '';
}

/** `cityCode` est en réalité un nom de ville en majuscules côté backend (ex. "DOUALA"), pas un référentiel. */
export function eventCityLabel(cityCode: string | null | undefined): string {
  if (!cityCode) return '';
  return cityCode.charAt(0) + cityCode.slice(1).toLowerCase();
}

export function eventRegionLabel(regionCode: string | null | undefined): string {
  if (!regionCode) return '';
  return reverseLookup(REGION_CODES, regionCode) || regionCode;
}

export function eventTypeLabel(eventType: string | null | undefined): string {
  if (!eventType) return '';
  return EVENT_TYPE_LABELS[eventType] ?? eventCityLabel(eventType);
}

export function eventCategoryLabel(categoryCode: string | null | undefined): string {
  if (!categoryCode) return '';
  return CATEGORY_LABELS[categoryCode] ?? eventCityLabel(categoryCode);
}

/** Clé i18n (`events.types.*`, public/i18n/*.json) pour un type d'événement connu, sinon null (repli sur `eventTypeLabel`). */
export function eventTypeLabelKey(eventType: string | null | undefined): string | null {
  if (!eventType || !(eventType in EVENT_TYPE_LABELS)) return null;
  return `events.types.${eventType.toLowerCase()}`;
}

/** Clé i18n (`events.categories.*`, public/i18n/*.json) pour une catégorie connue, sinon null (repli sur `eventCategoryLabel`). */
export function eventCategoryLabelKey(categoryCode: string | null | undefined): string | null {
  if (!categoryCode || !(categoryCode in CATEGORY_LABELS)) return null;
  return `events.categories.${categoryCode.toLowerCase()}`;
}

export function eventRegistrationStatusLabel(status: UpcomingEventRegistrationStatus): string {
  return REGISTRATION_STATUS_LABELS[status];
}

/** Clé i18n (`events.registrationStatus.*`, public/i18n/*.json) — voir `eventRegistrationStatusLabel` pour le repli non traduit. */
export function eventRegistrationStatusLabelKey(status: UpcomingEventRegistrationStatus): string {
  return `events.registrationStatus.${status.toLowerCase()}`;
}

export function eventRegistrationStatusTone(status: UpcomingEventRegistrationStatus): BadgeTone {
  return REGISTRATION_STATUS_TONES[status];
}

export function eventStatusLabel(status: UpcomingEventStatus): string {
  return EVENT_STATUS_LABELS[status];
}

export function eventStatusTone(status: UpcomingEventStatus): BadgeTone {
  return EVENT_STATUS_TONES[status];
}

/**
 * `startAt` est typé `string` (non-nullable) côté UpcomingEvent, mais l'admin peut publier un
 * événement sans date de début (aucune validation ne l'en empêchait avant ce correctif — voir
 * upcoming-events.ts#saveForm) : le backend accepte alors `null`, ce que `new Date(null)` résout
 * silencieusement au 1er janvier 1970. D'où la garde défensive ci-dessous, plutôt que de faire
 * confiance au type declaré à cette frontière API.
 */
function isValidDate(value: string | null | undefined): value is string {
  return !!value && !Number.isNaN(new Date(value).getTime());
}

/**
 * `locale` (BCP-47, ex. 'en-GB') est optionnel et retombe sur 'fr-FR' : les espaces authentifiés
 * (jeune/partenaire/admin) n'ont pas encore été reliés à `LanguageService.localeCode` et
 * continuent d'afficher un formatage français, hors périmètre de cette traduction publique.
 */
export function eventMonthLabel(startAt: string, locale = 'fr-FR'): string {
  if (!isValidDate(startAt)) return '—';
  return new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(startAt)).replace('.', '').toUpperCase();
}

export function eventDayLabel(startAt: string, locale = 'fr-FR'): string {
  if (!isValidDate(startAt)) return '—';
  return new Intl.DateTimeFormat(locale, { day: '2-digit' }).format(new Date(startAt));
}

export function eventFullDateLabel(startAt: string, locale = 'fr-FR'): string {
  if (!isValidDate(startAt)) return 'Date non renseignée';
  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'long', year: 'numeric' }).format(new Date(startAt));
}

/**
 * Ville + lieu (ou repli en ligne) — factorisé ici car dupliqué (home, events-list) avant
 * l'introduction d'app-event-card. `onlineLabel` par défaut en français pour les appelants pas
 * encore reliés à la traduction (voir `eventDateRangeLabel` ci-dessus pour le même principe).
 */
export function eventLocationSummary(event: UpcomingEvent, onlineLabel = 'En ligne'): string {
  const city = eventCityLabel(event.cityCode);
  if (event.venueName && city) return `${city} · ${event.venueName}`;
  if (city) return city;
  if (event.venueName) return event.venueName;
  return event.onlineUrl ? onlineLabel : '';
}

/** Libellé compact pour une carte événement, ex. "15-17 OCT 2026" ou "15 OCT 2026" si un seul jour. */
export function eventCompactDateLabel(startAt: string, endAt: string | null, locale = 'fr-FR'): string {
  if (!isValidDate(startAt)) return '';
  const start = new Date(startAt);
  const monthLabel = new Intl.DateTimeFormat(locale, { month: 'short' }).format(start).replace('.', '').toUpperCase();
  const year = start.getFullYear();
  if (!isValidDate(endAt)) return `${start.getDate()} ${monthLabel} ${year}`;
  const end = new Date(endAt as string);
  if (start.toDateString() === end.toDateString()) return `${start.getDate()} ${monthLabel} ${year}`;
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  if (sameMonth) return `${start.getDate()}-${end.getDate()} ${monthLabel} ${year}`;
  const endMonthLabel = new Intl.DateTimeFormat(locale, { month: 'short' }).format(end).replace('.', '').toUpperCase();
  return `${start.getDate()} ${monthLabel} - ${end.getDate()} ${endMonthLabel} ${year}`;
}

/** Libellé complet "jour date · heure" à "→ jour date heure" si l'événement s'étale sur plusieurs jours. */
export function eventDateRangeLabel(startAt: string, endAt: string | null, locale = 'fr-FR'): string {
  if (!isValidDate(startAt)) return 'Date non renseignée';
  const start = new Date(startAt);
  const dayFormatter = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const timeFormatter = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' });
  let label = `${dayFormatter.format(start)} · ${timeFormatter.format(start)}`;
  if (isValidDate(endAt)) {
    const end = new Date(endAt);
    const sameDay = start.toDateString() === end.toDateString();
    label += sameDay ? ` – ${timeFormatter.format(end)}` : ` → ${dayFormatter.format(end)} ${timeFormatter.format(end)}`;
  }
  return label;
}
